import { uuid } from '../../../../../helpers/helpers';

const emptyHoursCell = () => ({
  hours: 0,
  otHours: 0,
  doubleOTHours: 0,
});

export function buildWizardColumns(selectedCostcodeSelections) {
  if (!selectedCostcodeSelections?.length) return [];

  return selectedCostcodeSelections.map((sel) => ({
    rowId: uuid(),
    project: sel.project ?? null,
    phase: sel.phase ?? null,
    costcode: sel.costcode ?? null,
  }));
}

export function getCostcodeColumnKey(col) {
  const costcodeId = col.costcode?.id ?? col.costcodeId ?? '';
  const projectId = col.project?.id ?? '';
  const phaseId = col.phase?.id ?? 'unphased';
  return `${projectId}-${phaseId}-${costcodeId}`;
}

export function getPhaseProjectCostcodeKey({ projectId, costcodeId } = {}) {
  return `${projectId ?? ''}-${costcodeId ?? ''}`;
}

export function buildPhaseMapByProjectCostcode(phases = []) {
  return phases.reduce((acc, phase) => {
    if (!phase?.projectId || !phase?.costcodeId) return acc;
    const key = getPhaseProjectCostcodeKey(phase);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(phase);
    return acc;
  }, {});
}

export function expandCostcodePickerRows(costcode, phaseMap = {}) {
  if (!costcode?.phased || !costcode?.projectId) {
    return [{ ...costcode, phaseId: costcode?.phaseId ?? null }];
  }

  const matchingPhases = phaseMap[getPhaseProjectCostcodeKey({
    projectId: costcode.projectId,
    costcodeId: costcode.id,
  })] || [];

  if (!matchingPhases.length) return [];

  return matchingPhases.map((phase) => ({
    ...costcode,
    phaseId: phase.id,
  }));
}

export function formatCostcodePickerLabel(costcode, phaseMap = {}) {
  const baseLabel = costcode.code ? `${costcode.code} - ${costcode.name}` : costcode.name;
  if (!costcode.phaseId) return baseLabel;
  const phaseName = phaseMap[costcode.phaseId]?.name;
  return phaseName ? `${baseLabel} (${phaseName})` : baseLabel;
}

export function getCostcodePickerRowKey(costcode, projectKey) {
  const projectId = projectKey && projectKey !== 'Global' ? projectKey : '';
  const phaseId = costcode.phaseId ?? 'unphased';
  return `${projectId}-${phaseId}-${costcode.id}`;
}

export function columnsToWizardCostcodeSelections(columns = []) {
  return columns
    .filter((col) => col.costcode?.id)
    .map((col) => ({
      costcodeId: col.costcode.id,
      project: col.project ?? null,
      phase: col.phase ?? null,
      costcode: {
        id: col.costcode.id,
        name: col.costcode.name,
        code: col.costcode.code,
        fullText: col.costcode.fullText
          || [col.costcode.code, col.costcode.name].filter(Boolean).join(' ').trim(),
      },
    }));
}

export function mergeCostcodeSelectionsIntoGridState({
  prevColumns = [],
  prevValues = [],
  selections = [],
}) {
  const prevByKey = new Map();
  prevColumns.forEach((col) => {
    const key = getCostcodeColumnKey(col);
    if (key) prevByKey.set(key, col);
  });

  const nonNullSelections = selections || [];
  const newCostcodeColumns = nonNullSelections.map((sel) => {
    const key = getCostcodeColumnKey(sel);
    const existing = prevByKey.get(key);
    return {
      rowId: existing ? existing.rowId : uuid(),
      project: sel.project ?? null,
      phase: sel.phase ?? null,
      costcode: sel.costcode,
    };
  });

  const selectionKeys = new Set(nonNullSelections.map(getCostcodeColumnKey));
  const removedRowIds = new Set();
  prevColumns.forEach((col) => {
    if (!col.costcode?.id) return;
    const key = getCostcodeColumnKey(col);
    if (key && !selectionKeys.has(key)) removedRowIds.add(col.rowId);
  });

  const nonCostcodeColumns = prevColumns.filter((col) => !col.costcode?.id);
  const columns = [...newCostcodeColumns, ...nonCostcodeColumns];

  const nextValues = (prevValues || []).map((row) => {
    const next = { ...row };
    removedRowIds.forEach((rid) => {
      delete next[rid];
    });
    newCostcodeColumns.forEach((col) => {
      if (next[col.rowId] === undefined) {
        next[col.rowId] = emptyHoursCell();
      }
    });
    return next;
  });

  return { columns, values: nextValues };
}

export function buildWizardRows(selectedUsers, selectedEquipment, columnRowIds) {
  const values = [];

  (selectedUsers || []).forEach((u) => {
    if (!u?.id) return;
    const row = {
      id: u.id,
      name: u.name,
      type: 'user',
    };
    columnRowIds.forEach((colKey) => {
      row[colKey] = emptyHoursCell();
    });
    values.push(row);
  });

  (selectedEquipment || []).forEach((eq) => {
    if (!eq?.id) return;
    const row = {
      id: eq.id,
      name: eq.name,
      type: 'equipment',
    };
    columnRowIds.forEach((colKey) => {
      row[colKey] = emptyHoursCell();
    });
    values.push(row);
  });

  return values;
}
